<script setup lang="ts">
// icons
import { faHeart } from '@fortawesome/pro-solid-svg-icons';

/********************
 * PROPS & EMITS     *
 ********************/
export interface CiMapMarkerProps {
  campsite: any;
  showEffect?: boolean;
  isFavorite?: boolean;
  isGray?: boolean;
}
const props = withDefaults(defineProps<CiMapMarkerProps>(), {
  showEffect: false,
  isFavorite: false,
  isGray: false,
});

/********************
 * COMPOSITIONS      *
 ********************/
const { visibilityProducts } = useVisibilityProducts();
</script>

<template>
  <div class="map-marker__container h-[55px] w-11" :class="{ 'map-marker__container--is-gray': props.isGray }">
    <div
      class="map-marker__animation size-full"
    >
      <div
        v-if="showEffect"
        class="map-marker__effect absolute block"
      />
      <div
        class="relative size-full"
        @click="$event.preventDefault()"
      >
        <svg
          class="map-marker__body absolute left-0 top-0 w-11 fill-white"
          width="44"
          height="54"
          viewBox="0 0 44 54"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M29.3262 42.7507C37.8747 39.7326 44 31.5816 44 22C44 9.84974 34.1503 0 22 0C9.84974 0 0 9.84974 0 22C0 31.5816 6.12534 39.7326 14.6738 42.7507L20.268 52.44C21.0378 53.7733 22.9623 53.7733 23.7321 52.44L29.3262 42.7507Z"
          />
        </svg>

        <CiRatingRadial
          :rating="parseFloat(campsite.rating_avg_overall)"
          :is-gray="props.isGray"
          class="map-marker__rating h-11 p-[3px]"
          font-class="font-medium"
        />

        <div
          v-if="isFavorite"
          class="absolute"
          :class="[
            visibilityProducts(props.campsite).hasConversionOptimization
              ? 'right-[10px] top-[-10px]'
              : '-top-2 right-[-2px]',
          ]"
        >
          <CiAwesomeIcon
            class="icon fill-danger"
            :icon="faHeart"
            ratio="0.6"
          />
        </div>
        <svg
          v-if="visibilityProducts(props.campsite).hasConversionOptimization"
          class="absolute -top-2 right-[-2px] z-20 fill-warning"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8 0.559998L10.7273 4.80616L15.6085 6.08786L12.4129 9.99384L12.7023 15.0321L8 13.2L3.29772 15.0321L3.5871 9.99384L0.391548 6.08786L5.27268 4.80616L8 0.559998Z"
          />
        </svg>

        <div v-if="props.campsite.fanclub" class="absolute top-[30px]">
          <img
            src="~/assets/svg/fanclub-badge.svg"
            alt=""
            width="44"
            height="19"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.map-marker__container {
  .no-touch &:hover,
  &.active {
    .map-marker__body {
      @apply fill-dark-green;
    }

    :deep(.rating-radial) {
      .rating-radial__circular {
        @apply bg-dark-green;
      }
      .circle-bg {
        @apply stroke-white/25;
      }
      .circle.circle--primary {
        @apply stroke-white;
      }

      .circular-text {
        @apply text-white;
      }
    }
  }

  &--is-gray {
    .map-marker__body {
      @apply fill-gray-30;
    }
  }
}

.map-marker__body {
  filter: drop-shadow(0px -2px 4px rgba(0, 0, 0, 0.25));
}

.map-marker__effect {
  @apply bg-dark-green/60 opacity-0 size-11 left-0 bottom-[-22px];
  animation: pulsate 10s ease-out infinite;
  border-radius: 50%;
  z-index: -1;
}

.map-marker__animation {
  animation-duration: 0.25s;
  animation-fill-mode: both;
  animation-name: fade;
}
</style>
